import { useEffect, useRef, useState } from "react";
import { api, SITE_HOST, type SmsLoginInit } from "../api";
import { Logo } from "../components/Logo";
import { SmsLink } from "../components/SmsLink";
import { SmsQrCode, isMobile } from "../components/SmsQrCode";

interface Props {
  onLogin: (me: { id: string; email: string }) => void;
}

type Mode = "signin" | "sms" | "forgot";

export function Login({ onLogin }: Props) {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [forgotSent, setForgotSent] = useState(false);

  async function submitSignin(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const me = await api.login(email, password);
      onLogin(me);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "login_failed");
    } finally {
      setBusy(false);
    }
  }

  async function submitForgot(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.requestPasswordReset(email);
      setForgotSent(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "request_failed");
    } finally {
      setBusy(false);
    }
  }

  if (mode === "forgot") {
    if (forgotSent) {
      return (
        <div className="login-shell">
          <div className="login-card">
            <h1>Check your email</h1>
            <p className="muted">
              If an admin account exists for <strong>{email}</strong>, we've sent a reset link.
              The link expires in 1 hour.
            </p>
            <div style={{ marginTop: 16 }}>
              <button
                type="button"
                onClick={() => {
                  setMode("signin");
                  setForgotSent(false);
                  setErr(null);
                }}
              >
                Back to sign-in
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="login-shell">
        <form className="login-card" onSubmit={submitForgot}>
          <h1>Reset password</h1>
          <p className="muted">Enter the email tied to your admin account.</p>
          <label>Email</label>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
            <button type="submit" disabled={busy}>
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("signin");
                setErr(null);
              }}
            >
              Cancel
            </button>
          </div>
          {err && <div className="error">{err}</div>}
        </form>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <div className="login-card">
        <div className="login-brand">
          <Logo size={40} title={SITE_HOST} />
          <h1>
            login<span className="muted" style={{ fontWeight: 400 }}>.mioe.com.tr</span>
          </h1>
        </div>
        <p className="muted">Sign in to manage webapps and admins.</p>
        <div className="row" style={{ marginBottom: 12 }}>
          <button
            type="button"
            className={mode === "signin" ? "" : "secondary"}
            onClick={() => {
              setMode("signin");
              setErr(null);
            }}
          >
            Password
          </button>{" "}
          <button
            type="button"
            className={mode === "sms" ? "" : "secondary"}
            onClick={() => {
              setMode("sms");
              setErr(null);
            }}
          >
            SMS
          </button>
        </div>
        {mode === "signin" ? (
          <form onSubmit={submitSignin}>
            <label>Email</label>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <label>Password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div style={{ marginTop: 16 }}>
              <button type="submit" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </div>
            <div style={{ marginTop: 12 }}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setMode("forgot");
                  setErr(null);
                }}
              >
                Forgot password?
              </a>
            </div>
            {err && <div className="error">{err}</div>}
          </form>
        ) : (
          <SmsForm onLogin={onLogin} />
        )}
      </div>
    </div>
  );
}

function SmsForm({ onLogin }: Props) {
  const [phone, setPhone] = useState("");
  const [init, setInit] = useState<SmsLoginInit | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("");
  const [remaining, setRemaining] = useState<number>(0);

  useEffect(() => {
    if (!init) return;
    const target = Date.parse(init.expiresAt);
    const tick = () => setRemaining(Math.max(0, Math.round((target - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [init]);

  const pollRef = useRef<number | null>(null);
  useEffect(() => {
    if (!init) return;
    let cancelled = false;
    async function check() {
      try {
        const r = await api.smsLoginStatus(init!.sessionId, init!.pollToken);
        if (cancelled) return;
        if (r.status === "verified") {
          setStatus("Verified, signing in…");
          onLogin({ id: r.id, email: r.email });
          return;
        }
        if (r.status === "no_account") {
          // The init call answers the same way for every number now, so "this
          // phone isn't an admin" can only be learned here, after a real SMS.
          setStatus("No admin is registered with that number.");
          return;
        }
        if (r.status === "timeout") {
          setStatus("Code expired before SMS arrived. Try again.");
          return;
        }
        if (r.status === "failed") {
          setStatus("This code is no longer valid. Try again.");
          return;
        }
        pollRef.current = window.setTimeout(check, 2000);
      } catch (e) {
        if (cancelled) return;
        setStatus(e instanceof Error ? e.message : "poll_failed");
      }
    }
    pollRef.current = window.setTimeout(check, 1500);
    return () => {
      cancelled = true;
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [init, onLogin]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setStatus("");
    try {
      const r = await api.smsLoginInit(phone);
      setInit(r);
      setStatus("Waiting for your SMS…");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "init_failed");
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setInit(null);
    setStatus("");
    setErr(null);
  }

  if (init) {
    const body = `${init.code} ${init.otp}`;
    const mobile = isMobile();
    return (
      <div>
        <p className="muted">
          From the phone you registered, send the following SMS to your operator's inbound
          number:
        </p>
        <div className="code-display">{body}</div>
        {mobile ? (
          <SmsLink inboundNumber={init.inboundNumber} body={body} label="Open in SMS app" />
        ) : (
          <SmsQrCode inboundNumber={init.inboundNumber} body={body} />
        )}
        <p className="muted" style={{ marginTop: 12 }}>
          Expires in {remaining}s.
        </p>
        {status && <div className="muted" style={{ marginTop: 8 }}>{status}</div>}
        <div style={{ marginTop: 16 }}>
          <button type="button" className="secondary" onClick={reset}>
            Use a different number
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit}>
      <label>Phone</label>
      <input
        type="tel"
        autoComplete="tel"
        placeholder="+90 5xx xxx xx xx"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        required
      />
      <div style={{ marginTop: 16 }}>
        <button type="submit" disabled={busy}>
          {busy ? "Requesting…" : "Send code"}
        </button>
      </div>
      {err && <div className="error">{err}</div>}
    </form>
  );
}
